import { create } from 'zustand'

export interface DownloadItem {
	id: string
	filename: string
	percent: number
	bytesReceived: number
	totalBytes: number
	speed: number
	eta: number
	status: 'preparing' | 'downloading' | 'completed' | 'failed' | 'cancelled'
	error?: string
	abortController?: AbortController
	showProgress?: boolean
}

interface DownloadStore {
	downloads: DownloadItem[]
	isMinimized: boolean
	setMinimized: (minimized: boolean) => void
	addDownload: (
		download: Omit<DownloadItem, 'status' | 'percent' | 'bytesReceived' | 'totalBytes' | 'speed' | 'eta'>,
	) => void
	updateDownload: (id: string, updates: Partial<DownloadItem>) => void
	removeDownload: (id: string) => void
	clearCompleted: () => void
	cancelDownload: (id: string) => void
}

export const useDownloadStore = create<DownloadStore>((set, get) => ({
	downloads: [],
	isMinimized: false,
	setMinimized: (minimized: boolean) => set({ isMinimized: minimized }),
	addDownload: download =>
		set(state => ({
			downloads: [
				...state.downloads,
				{
					...download,
					status: 'preparing',
					percent: 0,
					bytesReceived: 0,
					totalBytes: 0,
					speed: 0,
					eta: 0,
				},
			],
			// Automatically expand the manager when a new download starts
			isMinimized: false,
		})),
	updateDownload: (id, updates) =>
		set(state => ({
			downloads: state.downloads.map(d => (d.id === id ? { ...d, ...updates } : d)),
		})),
	removeDownload: id =>
		set(state => ({
			downloads: state.downloads.filter(d => d.id !== id),
		})),
	clearCompleted: () =>
		set(state => ({
			downloads: state.downloads.filter(d => d.status === 'downloading' || d.status === 'preparing'),
		})),
	cancelDownload: id => {
		const download = get().downloads.find(d => d.id === id)
		if (download) {
			download.abortController?.abort()
			set(state => ({
				downloads: state.downloads.map(d => (d.id === id ? { ...d, status: 'cancelled' as const } : d)),
			}))
		}
	},
}))
